import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { keys } from '../../api/keys'
import { setDraftPhotos } from '../../api/drafts'
import type { Draft, DraftPhoto } from '../../api/threads'
import { t } from '../../i18n'

/**
 * The pictures that will arrive in the client's chat, shown before they do.
 *
 * A photograph is the part of a reply nobody can take back. The manager reads
 * the words and has to see what goes beside them -- and be able to drop the one
 * of the neighbour's bins without opening the object, editing its media and
 * coming back.
 *
 * Removing is the only edit offered here. Adding a picture means choosing from
 * an object's whole set, which is a screen of its own; what this needs to
 * prevent is a bad photograph going out, and that is a cross.
 */
export function DraftPhotos({ draft }: { readonly draft: Draft }) {
  const client = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const update = useMutation({
    mutationFn: (assetIds: readonly string[]) => setDraftPhotos(draft.id, assetIds),
    onSuccess: () => {
      setFailure(null)
      void client.invalidateQueries({ queryKey: keys.thread(draft.threadId) })
    },
    onError: (error: unknown) => {
      setFailure(error instanceof Error ? error.message : t('drafts.photosFailed'))
    },
  })

  if (draft.photos.length === 0) return null

  // Only while it can still be changed. On a sent draft this is a record of what
  // went out, and a cross next to it would be a button that cannot do anything.
  const editable = draft.approvable

  return (
    <section className="draft-photos">
      <div className="draft-photos__head">
        <span className="chips__label">
          {t('drafts.photos', { count: draft.photos.length })}
        </span>
        {editable ? <span className="draft-photos__hint">{t('drafts.photosHint')}</span> : null}
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <ul className="draft-photos__row">
        {draft.photos.map((photo) => (
          <li key={photo.assetId} className="draft-photos__item">
            <img
              className="draft-photos__image"
              src={photo.url}
              alt={photo.caption === '' ? t('drafts.photoAlt') : photo.caption}
              loading="lazy"
            />
            {editable ? (
              <Button
                variant="ghost"
                className="draft-photos__remove"
                disabled={update.isPending}
                title={t('drafts.photoRemove')}
                aria-label={t('drafts.photoRemove')}
                onClick={() => {
                  update.mutate(remaining(draft.photos, photo.assetId))
                }}
              >
                ×
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The list without one of them, order intact: the first picture is the one
 * everybody sees, so removing the second must not promote the third past it. */
function remaining(photos: readonly DraftPhoto[], removed: string): readonly string[] {
  return photos.filter((each) => each.assetId !== removed).map((each) => each.assetId)
}
